import { useTranslation } from 'react-i18next'
import type { ProductLawApplicability } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { isStaticLegislationBriefingLaw } from '@/v2/utils/staticLegislationBriefing'
import { applicabilityStatusCardClassName } from '@/v2/utils/productApplicabilitySummary'
import { StaticLegislationComingSoonBadge } from '../static-legislation-briefing/StaticLegislationComingSoonBadge'
import { ProductLawApplicabilityBadge } from '../ProductLawApplicabilityBadge'
import { parsePedClassification } from '@artem-lex/common-server/productApplicability/pedApplicability'
import { useV2ProductRegulationSection } from './context'

function V2ProductRegulationSectionHeaderLoading() {
  return (
    <>
      <Skeleton className='h-8 w-64 max-w-full' />
      <Skeleton className='h-4 w-full max-w-xl' />
      <Skeleton className='h-36 w-full' />
    </>
  )
}

function V2ProductRegulationSectionHeaderTitle() {
  const { law, lawLabel } = useV2ProductRegulationSection()
  const { t } = useTranslation('common')

  return (
    <div className='space-y-1'>
      <div className='flex flex-wrap items-center gap-2'>
        <h1 className='text-2xl font-bold tracking-tight'>{lawLabel}</h1>
        {isStaticLegislationBriefingLaw(law) ? (
          <StaticLegislationComingSoonBadge />
        ) : null}
      </div>
      <p className='text-muted-foreground'>
        {t(`v2.applicability.lawSummaries.${law}`)}
      </p>
    </div>
  )
}

function applicabilityNeedsUserInput(
  applicability: ProductLawApplicability | null
): boolean {
  if (applicability == null) {
    return true
  }

  return (
    applicability.status !== 'APPLICABLE' &&
    applicability.status !== 'NOT_APPLICABLE'
  )
}

function V2ProductRegulationSectionApplicabilityExplanation() {
  const { law, applicability, showRunningOverlay } =
    useV2ProductRegulationSection()
  const { t } = useTranslation('common')
  const status = applicability?.status ?? 'UNDECIDED'
  const isRunning = showRunningOverlay || applicability?.runState === 'running'
  const forceReason = applicability?.forceReason?.trim() ?? ''
  const isForced = applicability?.source === 'USER_FORCE'

  if (isRunning) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.applicability.section.explanations.running')}
      </p>
    )
  }

  let explanation = t(`v2.applicability.section.explanations.${status}`)
  const grillingSummary = applicability?.grillingSummary?.trim() ?? ''
  if (status === 'NEEDS_GRILLING' && grillingSummary !== '') {
    explanation = grillingSummary
  }
  if (isForced && forceReason !== '') {
    explanation = t('v2.applicability.section.explanations.forced', {
      reason: forceReason,
    })
  }

  const pedClassification =
    law === 'ped' && status === 'APPLICABLE' && !isForced
      ? parsePedClassification(applicability?.answers ?? null)
      : null

  return (
    <p className='text-sm text-muted-foreground'>
      {explanation}
      {pedClassification != null ? (
        <>
          {' '}
          {t('v2.applicability.ped.confirmedClassificationInline', {
            classification: t(
              `v2.applicability.ped.confirmation.classification.${pedClassification}`
            ),
          })}
        </>
      ) : null}
    </p>
  )
}

function V2ProductRegulationSectionApplicability() {
  const { applicability, openQuestionnaire } = useV2ProductRegulationSection()
  const { t } = useTranslation('common')
  const needsUserInput = applicabilityNeedsUserInput(applicability)

  let buttonLabel = t('v2.applicability.section.explanationButton')
  if (needsUserInput) {
    buttonLabel = t('v2.applicability.section.completeButton')
  }

  const hasApplicability = applicability != null
  const status = applicability?.status ?? 'UNDECIDED'

  return (
    <Card className={applicabilityStatusCardClassName(status)}>
      <CardContent className='flex items-center gap-2'>
        {hasApplicability && (
          <ProductLawApplicabilityBadge
            status={applicability.status}
            source={applicability.source}
            forceReason={applicability.forceReason}
            size='lg'
          />
        )}
        {!hasApplicability && (
          <ProductLawApplicabilityBadge status='UNDECIDED' size='lg' />
        )}
        <V2ProductRegulationSectionApplicabilityExplanation />
        <Button
          type='button'
          size='sm'
          className='ml-auto'
          onClick={openQuestionnaire}
        >
          {buttonLabel}
        </Button>
      </CardContent>
    </Card>
  )
}

function V2ProductRegulationSectionHeaderLoaded() {
  return (
    <div className='space-y-4'>
      <V2ProductRegulationSectionHeaderTitle />
      <V2ProductRegulationSectionApplicability />
    </div>
  )
}

export function V2ProductRegulationSectionHeader() {
  const { isLoading } = useV2ProductRegulationSection()

  if (isLoading) {
    return <V2ProductRegulationSectionHeaderLoading />
  }

  return <V2ProductRegulationSectionHeaderLoaded />
}
